import PropTypes from "prop-types";
import * as React from "react";
import { useCurrentUser } from "../globalState";
import { useShopSwitcher } from "../hooks";
import { Avatar, Button, List, SearchInput, Typography } from "../themeV2";
import { getInitials } from "../util/text";
import styles from "./ShopSwitcherPopover.module.css";

const AddIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
    width="20"
    height="20"
  >
    <path
      d="M12 5v14M5 12h14"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const getShopType = (shopObj) => {
  if (
    !shopObj.is_auto_collection &&
    !shopObj.is_agent &&
    !shopObj.is_talent_coordinator &&
    !shopObj.group_shop
  ) {
    return "Talent";
  }
  if (shopObj.is_auto_collection || shopObj.group_shop) {
    return "Collection";
  }
  if (shopObj.is_agent) {
    return "Agent";
  }
  if (shopObj.is_talent_coordinator) {
    return "Talent Coordinator";
  }
  return "";
};

ShopSwitcherPopover.displayName = "ShopSwitcherPopover";
ShopSwitcherPopover.propTypes = {
  onClose: PropTypes.func.isRequired,
  onAddShop: PropTypes.func.isRequired,
};

export default function ShopSwitcherPopover({ onClose, onAddShop }) {
  const currentUser = useCurrentUser();

  const {
    shops,
    searchQuery,
    setSearchQuery,
    noResults,
    switchToShop,
    activeInfluencer,
  } = useShopSwitcher();

  const handleClose = React.useCallback(() => {
    onClose();
    setSearchQuery("");
  }, [onClose, setSearchQuery]);

  const handleShopClick = React.useCallback(
    (route, isAgent, isTc) => () => {
      handleClose();
      switchToShop(route, isAgent, isTc);
    },
    [handleClose, switchToShop],
  );

  return (
    <>
      <div className={styles.searchWrap}>
        <SearchInput
          value={searchQuery}
          onValueChange={setSearchQuery}
          placeholder="Shop name..."
          label="Search shops"
          autoFocus
        />
      </div>

      <List.Root
        className={styles.shopList}
        role="listbox"
        aria-label="Available shops"
      >
        {!noResults &&
          shops.map(
            ({
              avatar_url,
              name,
              route,
              influencer_id,
              is_agent,
              is_talent_coordinator,
              is_auto_collection,
              group_shop,
            }) => {
              const isCurrent =
                activeInfluencer?.influencerId === influencer_id;
              return (
                <List.ItemButton
                  key={influencer_id}
                  role="option"
                  aria-selected={isCurrent}
                  className={
                    isCurrent ? styles.shopOptionCurrent : styles.shopOption
                  }
                  leading={
                    <Avatar
                      src={avatar_url}
                      alt=""
                      size="sm"
                      className={styles.shopAvatar}
                    >
                      {getInitials(name)}
                    </Avatar>
                  }
                  secondary={
                    <Typography variant="label-lg" color="variant" truncate>
                      {getShopType({
                        is_agent,
                        is_talent_coordinator,
                        is_auto_collection,
                        group_shop,
                      })}
                    </Typography>
                  }
                  onClick={handleShopClick(
                    route,
                    is_agent,
                    is_talent_coordinator,
                  )}
                >
                  <Typography
                    variant="body-lg"
                    truncate
                    className={isCurrent ? styles.currentName : styles.shopName}
                  >
                    {name}
                  </Typography>
                </List.ItemButton>
              );
            },
          )}

        {noResults && (
          <List.Item>No results for &ldquo;{searchQuery}&rdquo;.</List.Item>
        )}
      </List.Root>

      {currentUser.isAdmin && !activeInfluencer?.isTcShop && (
        <div className={styles.addWrap}>
          <Button
            variant="outlined"
            fullWidth
            leading={<AddIcon />}
            onClick={() => {
              onClose();
              onAddShop();
            }}
          >
            Add Shop
          </Button>
        </div>
      )}
    </>
  );
}
